<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ product.label }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_inventories') != -1" @click="editRow" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_inventories') != -1" @click="deleteRow" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
              {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Product Info -->
      <div v-else-if="product && product.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <!-- <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'inventories'}" @click="step = 'inventories'">{{$t('inventories')}}</a>
          </li> -->
          <li class="nav-item" v-if="helper.checkAccess('view_quotes') != -1">
            <a class="nav-link" :class="{active: step === 'quote'}" @click="step = 'quote'">{{$t('quote')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_deliveries') != -1">
            <a class="nav-link" :class="{active: step === 'deliveries'}" @click="step = 'deliveries'">{{$t('deliveries')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_purchases') != -1">
            <a class="nav-link" :class="{active: step === 'purchases'}" @click="step = 'purchases'">{{$t('purchases')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_receptions') != -1">
            <a class="nav-link" :class="{active: step === 'receptions'}" @click="step = 'receptions'">{{$t('receptions')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_supplier_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'supplier_invoices'}" @click="step = 'supplier_invoices'">{{$t('supplier_invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_supplier_returns') != -1">
            <a class="nav-link" :class="{active: step === 'supplier_returns'}" @click="step = 'supplier_returns'">{{$t('supplier_returns')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row g-4 align-items-start">
            <div class="col-lg-8">
              <div class="card product-insights-card mb-4">
                <div class="card-header bg-white product-insights-header">
                  <div>
                    <h6 class="mb-1"><AppIcon name="bi-bar-chart-line" class="bi bi-bar-chart-line product-section-icon" />{{$t('product')}} · {{$t('overview')}}</h6>
                    <small class="text-muted">{{$t('period')}}: {{formattedInsightsPeriod}}</small>
                  </div>
                  <div class="product-insights-filters">
                    <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm" :aria-label="$t('filter_by_date')">
                      <option value="all_time">{{$t('all_time')}}</option>
                      <option value="today">{{$t('today')}}</option>
                      <option value="this_month">{{$t('this_month')}}</option>
                      <option value="last_30_days">{{$t('last_30_days')}}</option>
                      <option value="custom">{{$t('custom_date')}}</option>
                    </select>
                    <date-picker v-if="dateRangeOption === 'custom'" v-model="range" type="date" range :clearable="true" :editable="false" :multi-calendars="1" :placeholder="$t('select_date_range')" @change="handleCustomRangeChange" class="product-insights-date-picker"></date-picker>
                  </div>
                </div>
                <div class="card-body position-relative">
                  <div v-if="insightsLoading" class="product-insights-loading"><div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div></div>
                  <div class="row g-2">
                    <div class="col-sm-6"><div class="product-metric"><span class="product-metric-icon product-metric-icon--stock"><AppIcon name="bi-box-seam" class="bi bi-box-seam" /></span><div><small>{{$t('current_quantity')}}</small><strong>{{formatQuantity(productInsights.current_stock)}} {{productDisplayUnit}}</strong><span v-if="productInsights.low_stock" class="text-danger">{{$t('low_stock_alert')}}</span></div></div></div>
                    <div class="col-sm-6"><div class="product-metric"><span class="product-metric-icon product-metric-icon--sales"><AppIcon name="bi-cash" class="bi bi-cash" /></span><div><small>{{$t('total_sales')}}</small><strong>{{formatMoney(productInsights.total_sales)}}</strong></div></div></div>
                    <div class="col-sm-6"><div class="product-metric"><span class="product-metric-icon product-metric-icon--sold"><AppIcon name="bi-cart-check" class="bi bi-cart-check" /></span><div><small>{{$t('quantity_sold')}}</small><strong>{{formatQuantity(productInsights.quantity_sold)}} {{productDisplayUnit}}</strong></div></div></div>
                    <div class="col-sm-6"><div class="product-metric"><span class="product-metric-icon product-metric-icon--value"><AppIcon name="bi-calculator" class="bi bi-calculator" /></span><div><small>{{$t('stock_value')}}</small><strong>{{formatMoney(productInsights.stock_value)}}</strong></div></div></div>
                  </div>
                </div>
              </div>

              <div class="card product-overview-card mb-4">
                <div class="card-header bg-white d-flex justify-content-between align-items-center"><h6 class="mb-0"><AppIcon name="bi-receipt" class="bi bi-receipt me-2" />{{$t('sales')}}</h6><span class="badge bg-light text-dark">{{productInsights.total_sales_count}} {{$t('sales')}}</span></div>
                <div class="product-table-wrap">
                  <table class="table product-insights-table mb-0">
                    <thead><tr><th>{{$t('reference')}}<small>{{$t('date')}}</small></th><th>{{$t('client')}}</th><th>{{$t('quantity')}}</th><th>{{$t('total')}}</th></tr></thead>
                    <tbody><tr v-for="sale in productInsights.sales" :key="`${sale.source}-${sale.id}`"><td><strong>{{sale.reference || '-'}}</strong><small>{{formatDate(sale.date)}}</small></td><td>{{sale.client || '-'}}</td><td>{{formatQuantity(sale.quantity)}} {{productDisplayUnit}}</td><td class="fw-semibold">{{formatMoney(sale.total)}}</td></tr></tbody>
                  </table>
                </div>
                <div v-if="!productInsights.sales.length" class="product-empty-state">{{$t('no_sales_for_period')}}</div>
              </div>

              <div class="card product-overview-card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center"><h6 class="mb-0"><AppIcon name="bi-clock-history" class="bi bi-clock-history me-2" />{{$t('stock_movement_history')}}</h6><span class="badge bg-light text-dark">{{productInsights.total_movements_count}}</span></div>
                <div class="product-table-wrap">
                  <table class="table product-insights-table mb-0">
                    <thead><tr><th>{{$t('date')}}</th><th>{{$t('action')}}</th><th>{{$t('reference')}}</th><th>{{$t('quantity')}}</th><th>{{$t('current_quantity')}}</th></tr></thead>
                    <tbody><tr v-for="movement in productInsights.movements" :key="movement.id"><td>{{formatDate(movement.date)}}</td><td>{{$t(movement.action)}}</td><td>{{movement.reference}}</td><td>{{movement.value === null ? '-' : formatQuantity(movement.value)}}</td><td class="fw-semibold">{{formatQuantity(movement.quantity)}}</td></tr></tbody>
                  </table>
                </div>
                <div v-if="!productInsights.movements.length" class="product-empty-state">{{$t('no_history_found')}}</div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="card product-overview-card mb-4">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-box" class="bi bi-box me-2" />{{$t('product')}} {{$t('information')}}</h6></div>
                <div class="card-body"><dl class="product-info-list mb-0"><div><dt>{{$t('product')}}</dt><dd><span>{{product.label}}</span><small>{{product.reference}}</small></dd></div><div v-if="product.sku"><dt>{{$t('sku')}}</dt><dd>{{product.sku}}</dd></div><div v-if="product.category"><dt>{{$t('category')}}</dt><dd>{{product.category.name}}</dd></div><div v-if="product.supplier"><dt>{{$t('supplier')}}</dt><dd>{{product.supplier.name}}</dd></div><div v-if="visibleColumns.unity"><dt>{{$t('unity')}}</dt><dd>{{productDisplayUnit}}</dd></div></dl><div v-if="product.notes" class="product-notes"><small class="text-muted d-block mb-1">{{$t('notes')}}</small><p class="mb-0">{{product.notes}}</p></div></div>
              </div>
              <div class="card product-overview-card" v-if="product.image"><div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-image" class="bi bi-image me-2" />{{$t('image')}}</h6></div><div class="card-body text-center"><img :src="product.image" class="product-overview-image" alt="Product Image"></div></div>
            </div>
          </div>

          <div v-if="product.has_variants && product.variants" class="card product-overview-card mt-4">
            <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-tag" class="bi bi-tag me-2" />{{$t('product_variants')}}</h6></div>
            <div class="table-responsive"><table class="table product-insights-table mb-0"><thead><tr><th>{{$t('variant_name')}}</th><th>{{$t('sku')}}</th><th>{{$t('barcode')}}</th><th>{{$t('price')}}</th></tr></thead><tbody><tr v-for="variant in product.variants" :key="variant.id"><td><strong>{{variant.name || '-'}}</strong></td><td>{{variant.sku || '-'}}</td><td>{{variant.barcode || '-'}}</td><td>{{formatMoney(variant.price)}}</td></tr></tbody></table></div>
          </div>

          <div class="card product-overview-card mt-4">
            <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-boxes" class="bi bi-boxes me-2" />{{$t('inventories')}}</h6></div>
            <div class="card-body"><inventory-component :product_id="product.id"></inventory-component></div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'inventories' && helper.checkAccess('view_inventories') != -1">
          <inventory-component :product_id="product.id" :isDetailClient="true"></inventory-component>
        </div>

        <div v-if="step === 'quote' && helper.checkAccess('view_quotes') != -1">
          <quote-component v-if="product && product.id" :product_id="product.id" :isDetailClient="true"></quote-component>
        </div>

        <div v-if="step === 'deliveries' && helper.checkAccess('view_deliveries') != -1">
          <delivery-component v-if="product && product.id" :product_id="product.id" :isDetailClient="true"></delivery-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_invoices') != -1">
          <invoice-component v-if="product && product.id" :product_id="product.id" :isDetailClient="true"></invoice-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="product.id" entity_type="product" />
        </div>

        <div v-if="step === 'purchases' && helper.checkAccess('view_purchases') != -1">
          <purchase-component v-if="product && product.id" :product_id="product.id" :isDetailSupplier="true"></purchase-component>
        </div>

        <div v-if="step === 'receptions' && helper.checkAccess('view_receptions') != -1">
          <reception-component v-if="product && product.id" :product_id="product.id" :isDetailSupplier="true"></reception-component>
        </div>

        <div v-if="step === 'supplier_invoices' && helper.checkAccess('view_supplier_invoices') != -1">
          <supplier-invoice-component v-if="product && product.id" :product_id="product.id" :isDetailSupplier="true"></supplier-invoice-component>
        </div>

        <div v-if="step === 'supplier_returns' && helper.checkAccess('view_supplier_returns') != -1">
          <supplier-return-component v-if="product && product.id" :product_id="product.id" :isDetailClient="true"></supplier-return-component>
        </div>
      </div>
    </div>


        <!-- Edit Modal -->
        <div class="modal fade" id="edit-product-modal" tabindex="-1" aria-labelledby="edit-product-modal" aria-hidden="true">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">
                <div class="modal-header bg-light py-2">
                    <h5 class="modal-title mb-0">
                        <AppIcon name="bi-pencil" class="bi bi-pencil me-2" />
                        {{$t('edit_product')}}
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                </div>
                <div v-if="editModalisOpen === true" class="modal-body bg-white py-3" style="text-align:left; max-height: 75vh; overflow-y: auto;">
                    
                    <div class="row">
                        <!-- Left Column: Form Fields -->
                        <div class="col-lg-8">
                            <!-- Basic Information -->
                            <div class="section-header-compact mb-2">
                                <h6 class="text-muted mb-0 d-flex align-items-center">
                                    <AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />
                                    {{$t('basic_information')}}
                                </h6>
                            </div>

                            <div class="row">
                                <div class="col-sm-7 mb-2">
                                    <label class="form-label-compact">
                                        {{$t('label')}} <span class="text-danger">*</span>
                                    </label>
                                    <input 
                                        v-model="product.label" 
                                        type="text" 
                                        class="form-control form-control-sm" 
                                        :placeholder="$t('name')"
                                    >
                                </div>
                                <div class="col-sm-5 mb-2">
                                    <label class="form-label-compact">{{$t('sku')}}</label>
                                    <input 
                                        v-model="product.sku" 
                                        type="text" 
                                        class="form-control form-control-sm" 
                                        :placeholder="$t('sku')"
                                    >
                                </div>
                                <div class="col-sm-4 mb-2" v-if="helper.checkModuleIsEnabled('suppliers')">
                                    <label class="form-label-compact">{{$t('supplier')}}</label>
                                    <auto-complete  
                                        v-if="createSuplierState === false" 
                                        @emitAutoCompleteSupplier="emitAutoCompleteSupplier" 
                                        :defaultValue="product?.supplier?.name"
                                        entity="suppliers"
                                        emitEvent="emitAutoCompleteSupplier"
                                    ></auto-complete>
                                    <a href="#" class="related-create-link d-block mt-1 small text-decoration-none" @click.prevent="openCreateSupplierModal">
                                        <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('create_new_supplier')}}
                                    </a>
                                </div>
                                <div class="col-sm-4 mb-2" v-if="helper.checkModuleIsEnabled('categories')"> 
                                    <label class="form-label-compact">{{$t('category')}}</label>
                                    <auto-complete  
                                        v-if="createCategoryState === false" 
                                        @emitAutoCompleteCategory="emitAutoCompleteCategory" 
                                        :defaultValue="product?.category?.name"
                                        entity="categories"
                                        emitEvent="emitAutoCompleteCategory"
                                    ></auto-complete>
                                    <a href="#" class="related-create-link d-block mt-1 small text-decoration-none" @click.prevent="openCreateCategoryModal">
                                        <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('create_new_category')}}
                                    </a>
                                </div>
                                <div class="col-sm-4 mb-2" v-if="!product.has_variants">
                                    <label class="form-label-compact">{{$t('barcode')}}</label>
                                    <div class="input-group input-group-sm">
                                        <input 
                                            v-model="product.barcode" 
                                            type="text" 
                                            class="form-control" 
                                            :placeholder="$t('barcode')"
                                        >
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button" 
                                            @click="openBarcodeModal"
                                            :title="$t('barcode_options')"
                                        >
                                            <AppIcon name="bi-upc-scan" class="bi bi-upc-scan" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Pricing -->
                            <div class="section-header-compact mb-2 mt-3">
                                <h6 class="text-muted mb-0 d-flex align-items-center">
                                    <AppIcon name="bi-cash-coin" class="bi bi-cash-coin me-2" />
                                    {{$t('pricing')}}
                                </h6>
                            </div>

                            <div v-if="!product.has_variants" class="card bg-light border-0 mb-2 p-2">
                                <div class="form-check form-switch mb-0">
                                    <input 
                                        class="form-check-input" 
                                        type="checkbox" 
                                        id="toggleTTCEdit" 
                                        v-model="product.price_includes_tax"
                                    >
                                    <label class="form-check-label small" for="toggleTTCEdit">
                                        {{ product.price_includes_tax ? $t('price_with_tax') : $t('price_without_tax') }}
                                    </label>
                                </div>
                            </div>

                            <div class="row" v-if="!product.has_variants && product.price_includes_tax">
                                <div class="col-sm-6 mb-2">
                                    <label class="form-label-compact">{{$t('price_with_tax')}} <span class="text-danger">*</span></label>
                                    <div class="input-group input-group-sm">
                                        <input 
                                            v-model.number="product.price_with_tax" 
                                            type="number" 
                                            class="form-control" 
                                            min="0" 
                                            step="0.01" 
                                            inputmode="decimal"
                                            @input="calculatePriceFromTTC"
                                        >
                                        <span class="input-group-text">{{business.currency}}</span>
                                    </div>
                                </div>
                                <div class="col-sm-6 mb-2">
                                    <label class="form-label-compact">{{$t('vat')}} %</label>
                                    <input 
                                        v-model.number="product.vat" 
                                        type="number" 
                                        class="form-control form-control-sm" 
                                        min="0" 
                                        max="100" 
                                        step="0.01" 
                                        inputmode="decimal"
                                        @input="calculatePriceFromTTC"
                                    >
                                </div>
                            </div>

                            <div class="row" v-if="!product.has_variants">
                                <div class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('purchase_price')}} <span class="text-danger">*</span></label>
                                    <div class="input-group input-group-sm">
                                        <input 
                                            v-model.number="product.purchase_price" 
                                            type="number" 
                                            class="form-control" 
                                            min="0" 
                                            step="0.01" 
                                            inputmode="decimal"
                                        >
                                        <span class="input-group-text">{{business.currency}}</span>
                                    </div>
                                </div>
                                <div class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('price')}} (HT) <span class="text-danger">*</span></label>
                                    <div class="input-group input-group-sm">
                                        <input 
                                            v-model.number="product.price" 
                                            type="number" 
                                            class="form-control" 
                                            min="0" 
                                            step="0.01" 
                                            inputmode="decimal"
                                            :readonly="product.price_includes_tax"
                                            :class="{'bg-light': product.price_includes_tax}"
                                            @input="!product.price_includes_tax && calculateTTCFromPrice()"
                                        >
                                        <span class="input-group-text">{{business.currency}}</span>
                                    </div>
                                    <small v-if="product.price_includes_tax" class="text-muted" style="font-size: 0.7rem;">
                                        <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />{{$t('calculated_automatically')}}
                                    </small>
                                </div>
                                <div v-if="visibleColumns.profit_margin" class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('profit_margin')}}</label>
                                    <div class="input-group input-group-sm">
                                        <input 
                                            type="text" 
                                            class="form-control bg-light" 
                                            :value="calculateMargin()" 
                                            readonly
                                        >
                                        <span class="input-group-text">%</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Message when variants are enabled -->
                            <div v-if="product.has_variants" class="alert alert-info mb-2 py-2">
                                <small>
                                    <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                                    {{$t('pricing_managed_per_variant')}}
                                </small>
                            </div>

                            <!-- Inventory -->
                            <div class="section-header-compact mb-2 mt-3">
                                <h6 class="text-muted mb-0 d-flex align-items-center">
                                    <AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />
                                    {{$t('inventories')}}
                                </h6>
                            </div>

                            <!-- Single Product Inventory (shown when has_variants is false) -->
                            <div v-if="!product.has_variants && (visibleColumns.quantity || visibleColumns.warehouse || visibleColumns.unity)" class="row">
                                <div v-if="visibleColumns.unity" class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('unity')}}</label>
                                    <select v-model="product.unity" class="form-select form-select-sm">
                                        <option value="unit">{{$t('unit')}}</option>
                                        <option value="kg">kg</option>
                                        <option value="g">g</option>
                                        <option value="L">L</option>
                                        <option value="ml">ml</option>
                                        <option value="m">m</option>
                                        <option value="m²">m²</option>
                                    </select>
                                </div>
                                <div v-if="visibleColumns.unity && product.unity === 'm²'" class="col-sm-8 mb-2">
                                    <div class="card bg-light border-0 p-2">
                                        <label class="form-label-compact small text-primary mb-1">
                                            <AppIcon name="bi-calculator" class="bi bi-calculator me-1" />{{$t('carpet_area_helper')}}
                                        </label>
                                        <div class="row g-2">
                                            <div class="col-6">
                                                <div class="input-group input-group-sm">
                                                    <span class="input-group-text bg-white" style="font-size: 0.7rem;">W</span>
                                                    <input 
                                                        v-model.number="product.unity_width" 
                                                        type="number" 
                                                        class="form-control" 
                                                        step="0.001"
                                                        placeholder="Width"
                                                        @input="calculateCarpetArea"
                                                    >
                                                </div>
                                            </div>
                                            <div class="col-6">
                                                <div class="input-group input-group-sm">
                                                    <span class="input-group-text bg-white" style="font-size: 0.7rem;">L</span>
                                                    <input 
                                                        v-model.number="product.unity_length" 
                                                        type="number" 
                                                        class="form-control" 
                                                        step="0.001"
                                                        placeholder="Length"
                                                        @input="calculateCarpetArea"
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('quantity')}} <span class="text-danger">*</span></label>
                                    <input 
                                        v-model.number="product.quantity" 
                                        type="number" 
                                        class="form-control form-control-sm" 
                                        step="0.001"
                                        min="0"
                                        inputmode="decimal"
                                    >
                                </div>
                                <div class="col-sm-4 mb-2">
                                    <label class="form-label-compact">{{$t('min_quantity')}} <span class="text-danger">*</span></label>
                                    <input 
                                        v-model.number="product.min_quantity" 
                                        type="number" 
                                        class="form-control form-control-sm" 
                                        step="0.001"
                                        min="0"
                                        inputmode="decimal"
                                    >
                                </div>
                                <div class="col-sm-4 mb-2" v-if="helper.checkModuleIsEnabled('warehouses')">
                                    <label class="form-label-compact">{{$t('warehouse')}}</label>
                                    <auto-complete  
                                        v-if="createWarehouseState === false" 
                                        @emitAutoCompleteWarehouse="emitAutoCompleteWarehouse" 
                                        :defaultValue="product?.warehouse?.name"
                                        entity="warehouses"
                                        emitEvent="emitAutoCompleteWarehouse"
                                    ></auto-complete>
                                    <a href="#" class="related-create-link d-block mt-1 small text-decoration-none" @click.prevent="openCreateWarehouseModal">
                                        <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />{{$t('create_new_warehouse')}}
                                    </a>
                                </div>
                            </div>

                            <!-- Product Variants Section (shown when has_variants is true) -->
                            <div v-else class="variants-section">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <h6 class="mb-0 small text-muted">
                                        <AppIcon name="bi-tag" class="bi bi-tag me-1" />
                                        {{$t('product_variants')}}
                                    </h6>
                                    <button 
                                        type="button" 
                                        @click="addVariant" 
                                        class="btn btn-sm btn-outline-primary"
                                        style="padding: 4px 12px; font-size: 0.8rem;"
                                    >
                                        <AppIcon name="bi-plus-circle" class="bi bi-plus-circle me-1" />
                                        {{$t('add_variant')}}
                                    </button>
                                </div>

                                <!-- Variants List -->
                                <div v-if="product.variants.length === 0" class="alert alert-info mb-2 py-2 px-2">
                                    <small>
                                        <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                                        {{$t('no_variants_added_click_add_variant')}}
                                    </small>
                                </div>

                                <div v-for="(variant, index) in product.variants" :key="index" class="variant-card mb-2">
                                    <div class="card border">
                                        <div class="card-header bg-light py-2 px-2 d-flex justify-content-between align-items-center">
                                            <small class="fw-bold text-muted">
                                                <AppIcon name="bi-tag" class="bi bi-tag me-1" />
                                                {{$t('variant')}} #{{index + 1}}
                                            </small>
                                            <button 
                                                type="button" 
                                                @click="removeVariant(index)" 
                                                class="btn btn-sm btn-outline-danger"
                                                style="padding: 2px 8px; font-size: 0.75rem;"
                                            >
                                                <AppIcon name="bi-trash" class="bi bi-trash" />
                                            </button>
                                        </div>
                                        <div class="card-body p-2">
                                            <div class="row">
                                                <div class="col-md-6 mb-2">
                                                    <label class="form-label-compact">{{$t('variant_name')}} <span class="text-danger">*</span></label>
                                                    <input 
                                                        v-model="variant.name" 
                                                        type="text" 
                                                        class="form-control form-control-sm" 
                                                        :placeholder="$t('e_g_red_large_cotton')"
                                                    >
                                                    <small class="text-muted" style="font-size: 0.7rem;">{{$t('e_g_size_color_material')}}</small>
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('sku')}}</label>
                                                    <input 
                                                        v-model="variant.sku" 
                                                        type="text" 
                                                        class="form-control form-control-sm" 
                                                        :placeholder="$t('sku')"
                                                    >
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('barcode')}}</label>
                                                    <div class="input-group input-group-sm">
                                                        <input 
                                                            v-model="variant.barcode" 
                                                            type="text" 
                                                            class="form-control form-control-sm" 
                                                            :placeholder="$t('barcode')"
                                                        >
                                                        <button 
                                                            class="btn btn-outline-secondary dropdown-toggle" 
                                                            type="button" 
                                                            data-bs-toggle="dropdown" 
                                                            aria-expanded="false"
                                                            style="padding: 0.375rem 0.5rem; font-size: 0.75rem;"
                                                        >
                                                            <AppIcon name="bi-upc-scan" class="bi bi-upc-scan" />
                                                        </button>
                                                        <ul class="dropdown-menu dropdown-menu-end">
                                                            <li>
                                                                <a class="dropdown-item" href="#" @click.prevent="openVariantBarcodeModal(index, 'scan')">
                                                                    <AppIcon name="bi-camera" class="bi bi-camera me-2" />{{$t('scan_barcode')}}
                                                                </a>
                                                            </li>
                                                            <li>
                                                                <a class="dropdown-item" href="#" @click.prevent="generateVariantBarcode(index)">
                                                                    <AppIcon name="bi-upc" class="bi bi-upc me-2" />{{$t('generate_barcode')}}
                                                                </a>
                                                            </li>
                                                            <li v-if="variant.barcode">
                                                                <hr class="dropdown-divider">
                                                            </li>
                                                            <li v-if="variant.barcode">
                                                                <a class="dropdown-item" href="#" @click.prevent="downloadVariantBarcode(variant.barcode)">
                                                                    <AppIcon name="bi-download" class="bi bi-download me-2" />{{$t('download_barcode')}}
                                                                </a>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('purchase_price')}} <span class="text-danger">*</span></label>
                                                    <input 
                                                        v-model.number="variant.purchase_price" 
                                                        type="number" 
                                                        class="form-control form-control-sm" 
                                                        min="0" 
                                                        step="0.01"
                                                        inputmode="decimal"
                                                    >
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('price')}} <span class="text-danger">*</span></label>
                                                    <input 
                                                        v-model.number="variant.price" 
                                                        type="number" 
                                                        class="form-control form-control-sm" 
                                                        min="0" 
                                                        step="0.01"
                                                        inputmode="decimal"
                                                    >
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('quantity')}} <span class="text-danger">*</span></label>
                                                    <input 
                                                        v-model.number="variant.quantity" 
                                                        type="number" 
                                                        class="form-control form-control-sm" 
                                                        step="0.001"
                                                        min="0"
                                                        inputmode="decimal"
                                                    >
                                                </div>
                                                <div class="col-md-3 mb-2">
                                                    <label class="form-label-compact">{{$t('min_quantity')}}</label>
                                                    <input 
                                                        v-model.number="variant.min_quantity" 
                                                        type="number" 
                                                        class="form-control form-control-sm" 
                                                        step="0.001"
                                                        min="0"
                                                        inputmode="decimal"
                                                    >
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-12">
                                                    <label class="form-label-compact">{{$t('variant_image')}}</label>
                                                    <div class="variant-image-upload">
                                                        <div v-if="variant.image" class="position-relative d-inline-block">
                                                            <img :src="variant.image" class="variant-preview-image" style="height: 100px;width: 100px;" alt="Variant">
                                                            <button 
                                                                type="button" 
                                                                @click="variant.image = null" 
                                                                class="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                                                                style="padding: 2px 6px; font-size: 0.7rem;"
                                                            >
                                                                <AppIcon name="bi-x" class="bi bi-x" />
                                                            </button>
                                                        </div>
                                                        <div v-else>
                                                            <input 
                                                                type="file" 
                                                                :id="`variantFileInputEdit${index}`"
                                                                @change="handleVariantFileUpload($event, index)" 
                                                                accept="image/*" 
                                                                class="d-none"
                                                            >
                                                            <label 
                                                                :for="`variantFileInputEdit${index}`"
                                                                class="btn btn-sm btn-outline-secondary mb-0"
                                                                style="cursor: pointer;"
                                                            >
                                                                <AppIcon name="bi-image" class="bi bi-image me-1" />
                                                                {{$t('upload_image')}}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Notes -->
                            <div class="mb-2 mt-3">
                                <collapsible-notes-field v-model="product.notes" id="product-details-notes" rows="2" />
                            </div>
                        </div>

                        <!-- Right Column: Image Upload -->
                        <div class="col-lg-4 mb-2">
                            <label class="form-label-compact">{{$t('image')}}</label>
                            <div class="image-preview">
                                <div 
                                    class="drop-zone-compact" 
                                    @dragover.prevent
                                    @drop.prevent="handleFileDrop"
                                    @click="$refs.fileInputEdit.click()"
                                    :class="{'has-image': product.image}"
                                >
                                    <img v-if="product.image" :src="product.image" class="preview-image-compact" alt="Product" />
                                    <div v-else class="drop-zone-content">
                                        <AppIcon name="bi-cloud-upload" class="bi bi-cloud-upload" style="font-size: 32px;" />
                                        <p class="mt-2 mb-1">{{$t('drag_drop_or_click')}}</p>
                                        <small class="text-muted" style="font-size: 0.75rem;">{{$t('max_size_5mb')}}</small>
                                        <!-- <span v-if="business && business.status !== 'active'" class="badge bg-secondary mt-2">{{$t('pro')}}</span> -->
                                    </div>
                                </div>
                            </div>
                            <input 
                                type="file" 
                                ref="fileInputEdit" 
                                @change="handleFileUpload" 
                                accept="image/*" 
                                class="d-none"
                            >

                            <!-- Calculation info -->
                            <div v-if="!product.has_variants && product.price_includes_tax && product.price_with_tax && product.vat" class="mt-2">
                                <div class="card bg-light border-0">
                                    <div class="card-body p-2">
                                        <small class="text-muted d-block mb-1"><strong>{{$t('price_calculation')}}:</strong></small>
                                        <small class="text-muted d-block">{{$t('price_with_tax')}}: {{ formatMoney(product.price_with_tax) }}</small>
                                        <small class="text-muted d-block">{{$t('vat')}}: {{ product.vat }}%</small>
                                        <hr class="my-1">
                                        <small class="text-dark d-block"><strong>{{$t('price')}} (HT): {{ formatMoney(product.price) }}</strong></small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <div class="modal-footer bg-light py-2">
                    <button type="button" class="btn btn-sm btn-secondary" data-bs-dismiss="modal">
                        <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />
                        {{$t('close')}}
                    </button>
                    <button 
                        type="button" 
                        class="btn btn-sm btn-success" 
                        :disabled="loading || !isFormValid" 
                        @click="updateRow"
                    >
                        <AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />
                        {{loading ? $t('saving') : $t('edit')}}
                    </button>
                </div>
                </div>
            </div>
        </div>
        <!-- Edit modal -->

        <!-- Barcode Modal -->
        <div class="modal fade" id="barcode-modal" tabindex="-1" aria-labelledby="barcode-modal" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{$t('barcode_options')}}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div v-if="!barcodeScanning" class="d-grid gap-2">
                            <button 
                                class="btn btn-primary" 
                                @click="startBarcodeScanner"
                            >
                                <AppIcon name="bi-camera" class="bi bi-camera me-2" />{{$t('scan_barcode')}}
                            </button>
                            <button 
                                class="btn btn-secondary" 
                                @click="generateAndSetBarcode"
                            >
                                <AppIcon name="bi-upc" class="bi bi-upc me-2" />{{$t('generate_barcode')}}
                            </button>
                            <button 
                                v-if="product.barcode" 
                                class="btn btn-info" 
                                @click="downloadBarcode"
                            >
                                <AppIcon name="bi-download" class="bi bi-download me-2" />{{$t('download_barcode')}}
                            </button>
                        </div>
                        <div v-else>
                            <div class="text-center mb-3">
                                <video ref="barcodeVideo" autoplay playsinline style="width: 100%; max-width: 400px; border-radius: 8px;"></video>
                                <canvas ref="barcodeCanvas" style="display: none;"></canvas>
                            </div>
                            <button 
                                class="btn btn-danger w-100" 
                                @click="stopBarcodeScanner"
                            >
                                <AppIcon name="bi-x-circle" class="bi bi-x-circle me-2" />{{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Barcode Modal -->
          
        <add-supplier v-if="createSuplierState" :state="createSuplierState" @createSupplier="createSupplier"/>

        <add-category v-if="createCategoryState" :state="createCategoryState" @createCategory="createCategory"/>

        <add-warehouse v-if="createWarehouseState" :state="createWarehouseState" @createWarehouse="createWarehouse"/>

    </div>

</template>

<script>
import helper from '../../helper'
import quoteComponent from '../../components/QuoteComponent.vue'
import InvoiceComponent from '../../components/InvoiceComponent.vue'
import DeliveryComponent from '../../components/DeliveryComponent.vue'
import inventoryComponent from '../../components/InventoryComponent.vue'
import autoComplete from '../../components/autocomplete/AutoComplete'
import AddSupplier from '../../components/AddSupplier.vue'
import addCategory from '../../components/AddCategory.vue'
import addWarehouse from '../../components/AddWarehouse.vue'
import attachments from '../../components/Attachment.vue'
import PurchaseComponent from '../../components/PurchaseComponent.vue'
import ReceptionComponent from '../../components/ReceptionComponent.vue'
import SupplierInvoiceComponent from '../../components/SupplierInvoiceComponent.vue'
import SupplierReturnComponent from '../../components/SupplierReturnComponent.vue'
import DatePicker from 'vue2-datepicker'
import moment from 'moment'
import 'vue2-datepicker/index.css'
import { getInventoryProductColumnDefaults } from '../../constants/inventoryProductColumns'

const maxAllowedSize = 5 * 1024 * 1024; //5MB

export default {
    name: 'ProductDetails',
    components :{
        InvoiceComponent,
        DeliveryComponent,
        quoteComponent,
        inventoryComponent,
        autoComplete,
        AddSupplier,
        addWarehouse,
        addCategory,
        attachments,
        PurchaseComponent,
        ReceptionComponent,
        SupplierInvoiceComponent,
        SupplierReturnComponent,
        DatePicker
    },
    data(){
        return {
            product : {},
            inventories : [],
            count : 0,
            step : 'overview',
            editModal : null,
            editModalisOpen : false,
            createWarehouseState:false,
            createSuplierState : false,
            createCategoryState : false,
            loading: false,
            imageFile : null,
            barcodeModal: null,
            barcodeScanning: false,
            barcodeStream: null,
            barcodeAnimationFrame: null,
            currentVariantIndex: null,
            barcodeMode: null, // 'product' or 'variant'
            productInsights: {
                current_stock: 0,
                minimum_stock: 0,
                stock_value: 0,
                low_stock: false,
                total_sales: 0,
                quantity_sold: 0,
                total_sales_count: 0,
                sales: [],
                total_movements_count: 0,
                movements: []
            },
            dateRangeOption: 'this_month',
            range: [moment().startOf('month').toDate(), new Date()],
            start: moment().startOf('month').format('YYYY-MM-DD'),
            end: moment().format('YYYY-MM-DD'),
            insightsLoading: false,
            insightsRequestId: 0
        }
    },
    mounted(){
        window.mixpanel.track("product_details");
        //check permissions
        if(helper.checkAccess('view_inventories') == -1) return this.$router.push({path : '/'})
        if(helper.checkAccess('view_inventories') == -2) return this.$router.push({path : '/subscribe'})

        // eslint-disable-next-line no-undef
        this.editModal = new bootstrap.Modal(document.getElementById('edit-product-modal'))
        document.getElementById('edit-product-modal').addEventListener('hidden.bs.modal', () => { this.editModalisOpen = false })

        // eslint-disable-next-line no-undef
        this.barcodeModal = new bootstrap.Modal(document.getElementById('barcode-modal'), {backdrop: 'static', keyboard: false})
        document.getElementById('barcode-modal').addEventListener('hidden.bs.modal', () => { 
          this.stopBarcodeScanner()
        })
    },
    beforeUnmount() {
        // Clean up camera resources
        this.stopBarcodeScanner()
    },
    created(){
        if(!this.$route.params || !this.$route.params.id) return this.$route.push('/products')
        this.getProduct(this.$route.params.id)
        this.fetchProductInsights()
    },
    methods : {
        async getProduct(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/products/${id}`)
                this.product = response.data
                
                // Ensure variants array exists
                if (!this.product.variants) {
                    this.product.variants = []
                }
                
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/products'})
                this.loading = false
            }
        },
        async fetchProductInsights(){
            const requestId = ++this.insightsRequestId
            this.insightsLoading = true
            try{
                const params = new URLSearchParams()
                if(this.start && this.end){
                    params.append('start', this.start)
                    params.append('end', this.end)
                }
                const query = params.toString() ? `?${params.toString()}` : ''
                const response = await helper.customGet(`${process.env.VUE_APP_API}/products/${this.$route.params.id}/insights${query}`)
                if(requestId !== this.insightsRequestId) return

                this.productInsights = {
                    current_stock: Number(response.data.current_stock) || 0,
                    minimum_stock: Number(response.data.minimum_stock) || 0,
                    stock_value: Number(response.data.stock_value) || 0,
                    low_stock: Boolean(response.data.low_stock),
                    total_sales: Number(response.data.total_sales) || 0,
                    quantity_sold: Number(response.data.quantity_sold) || 0,
                    total_sales_count: Number(response.data.total_sales_count) || 0,
                    sales: Array.isArray(response.data.sales) ? response.data.sales : [],
                    total_movements_count: Number(response.data.total_movements_count) || 0,
                    movements: Array.isArray(response.data.movements) ? response.data.movements : []
                }
            }catch(error){
                if(requestId === this.insightsRequestId) this.$toast.error(this.$t('failed_to_fetch_data'))
            }finally{
                if(requestId === this.insightsRequestId) this.insightsLoading = false
            }
        },
        handleDateRangeChange(){
            const today = moment()
            if(this.dateRangeOption === 'all_time'){
                this.range = []
                this.start = null
                this.end = null
            }else if(this.dateRangeOption === 'today'){
                this.range = [today.toDate(), today.toDate()]
                this.start = today.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'this_month'){
                const monthStart = moment().startOf('month')
                this.range = [monthStart.toDate(), today.toDate()]
                this.start = monthStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'last_30_days'){
                const periodStart = moment().subtract(29, 'days')
                this.range = [periodStart.toDate(), today.toDate()]
                this.start = periodStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else{
                this.range = []
                return
            }
            this.fetchProductInsights()
        },
        handleCustomRangeChange(range){
            if(!range || !range[0] || !range[1]) return
            this.start = moment(range[0]).format('YYYY-MM-DD')
            this.end = moment(range[1]).format('YYYY-MM-DD')
            this.fetchProductInsights()
        },
        async getInventories(){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/inventories?product_id=${this.$route.params.id}`)
                this.inventories = response.data.data
                this.count = response.data.count
                this.loading = false
                
            }catch(error){
                this.loading = false
            }
        },
        emitAutoCompleteSupplier(data){
            this.product.supplier_id = data.result.id
            this.product.supplier = data.result
        },
        emitAutoCompleteCategory(data){
            this.product.category_id = data.result.id
        },
        createSupplier(data){
            if(data.supplier){
                this.product.supplier_id = data.supplier.id
                this.product.supplier = data.supplier
            }
            if(data && data.state != undefined){
                this.createSuplierState = data.state
            }
        },
        createCategory(data){
            if(data.category){
                this.product.category_id = data.category.id
                this.product.category_id = data.category.id
                this.product.category = data.category
            }
            if(data && data.state != undefined){
                this.createCategoryState = data.state
            }
        },
        openCreateSupplierModal(){
            this.createSuplierState = !this.createSuplierState
        },
        openCreateCategoryModal(){
            this.createCategoryState = !this.createCategoryState
        },
        openCreateWarehouseModal(){
            this.createWarehouseState = !this.createWarehouseState
        },
        emitAutoCompleteWarehouse(data){
            if(data.index != undefined){
                this.product.inventories[data.index].warehouse = data.result
                this.product.inventories[data.index].warehouse_id = data.result.id
            }else{
                this.product.warehouse = data.result
                this.product.warehouse_id = data.result.id
            }
        },
        createWarehouse(data){
            if(data.warehouse){
                this.product.warehouse_id = data.warehouse.id
                this.product.warehouse = data.warehouse
            }
            if(data && data.state != undefined){
                this.createWarehouseState = data.state
            }
        },
        editRow(){
            this.imageFile = null
            this.editModalisOpen = true;
            
            // Initialize unity and helper fields
            this.product.unity = this.product.unity || 'unit'
            this.product.unity_width = null
            this.product.unity_length = null
            
            // If product has variants, populate quantity and min_quantity from inventory
            if (this.product.has_variants && this.product.variants && this.product.variants.length > 0) {
                this.product.variants.forEach(variant => {
                    // If variant has inventory data, extract purchase_price and quantity
                    if (variant.inventory) {
                        variant.purchase_price = variant.inventory.purchase_price || 0
                        variant.quantity = variant.inventory.quantity || 0
                        variant.min_quantity = variant.inventory.min_quantity || 0
                    }
                })
            } else if (!this.product.has_variants && this.product.inventories && this.product.inventories.length > 0) {
                // For non-variant products, extract data from the first inventory
                const inventory = this.product.inventories[0]
                this.product.quantity = inventory.quantity || 0
                this.product.min_quantity = inventory.min_quantity || 0
                this.product.purchase_price = inventory.purchase_price || 0
                this.product.price = inventory.price || 0
                
                // Extract warehouse if available
                if (inventory.warehouse) {
                    this.product.warehouse = inventory.warehouse
                    this.product.warehouse_id = inventory.warehouse.id
                }
            }
            
            this.editModal.show()
        },
        async updateRow(){
            try{
                this.loading = true;
                
                // Prepare the payload
                const payload = { ...this.product }
                
                // If product has variants, clean up the variants array
                if (payload.has_variants && payload.variants && payload.variants.length > 0) {
                    payload.variants = payload.variants.map(variant => ({
                        id: variant.id,
                        name: variant.name,
                        sku: variant.sku,
                        barcode: variant.barcode,
                        price: parseFloat(variant.price) || 0,
                        purchase_price: parseFloat(variant.purchase_price) || 0,
                        quantity: Number(variant.quantity) || 0,
                        min_quantity: Number(variant.min_quantity) || 0,
                        image: variant.image
                    }))
                }
                
                // If product doesn't have variants, format the inventories array properly
                if (!payload.has_variants && payload.inventories && payload.inventories.length > 0) {
                    payload.inventories[0] = {
                        ...payload.inventories[0],
                        quantity: Number(payload.quantity) || 0,
                        min_quantity: Number(payload.min_quantity) || 0,
                        purchase_price: parseFloat(payload.purchase_price) || 0,
                        price: parseFloat(payload.price) || 0,
                        warehouse_id: payload.warehouse_id
                    }
                }
                
                await helper.customPatch(`${process.env.VUE_APP_API}/products/${this.product.id}`, payload)
                this.loading = false;
                this.editModal.hide() 
                this.getProduct(this.$route.params.id)
                this.fetchProductInsights()
            }catch(error){
                this.loading = false;
            }
        },
        calculateCarpetArea() {
            if (this.product.unity === 'm²') {
                const w = Number(this.product.unity_width) || 0;
                const l = Number(this.product.unity_length) || 0;
                if (w > 0 && l > 0) {
                    this.product.quantity = parseFloat((w * l).toFixed(3));
                }
            }
        },
        async deleteRow(row){
                this.$swal.fire({
                    title: `${this.$t('are_you_sure')}?`,
                    text : this.$t('you_cant_revert'),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: this.$t('yes_delete_it')
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        await helper.customDelete(`${process.env.VUE_APP_API}/products/${row.id}`)
                        this.$router.push({path : '/products'})
                    }
                })
        },
        // Calculate HT price from TTC price and VAT
        calculatePriceFromTTC() {
            if (this.product.price_with_tax && this.product.vat) {
                const priceWithTax = parseFloat(this.product.price_with_tax);
                const vatRate = parseFloat(this.product.vat) / 100;
                
                // Formula: HT = TTC / (1 + VAT_rate)
                this.product.price = (priceWithTax / (1 + vatRate)).toFixed(2);
            }
        },
        // Calculate TTC price from HT price and VAT
        calculateTTCFromPrice() {
            if (this.product.price && this.product.vat) {
                const price = parseFloat(this.product.price);
                const vatRate = parseFloat(this.product.vat) / 100;
                
                // Formula: TTC = HT * (1 + VAT_rate)
                this.product.price_with_tax = (price * (1 + vatRate)).toFixed(2);
            }
        },
        calculateMargin() {
            const purchase = parseFloat(this.product.purchase_price) || 0;
            const sale = parseFloat(this.product.price) || 0;
            
            if (purchase === 0 || sale === 0) return '0.00';
            
            const margin = ((sale - purchase) / sale) * 100;
            return margin.toFixed(2);
        },
        generateBarcodeString(){
            // Format: <productId><YYYY><MM><DD><HH><mm><ss>
            const idPart = this.product && this.product.id 
                ? String(this.product.id) 
                : String(Math.floor(100000 + Math.random() * 900000))
            const now = new Date()
            const yyyy = String(now.getFullYear())
            const MM = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const HH = String(now.getHours()).padStart(2, '0')
            const mm = String(now.getMinutes()).padStart(2, '0')
            const ss = String(now.getSeconds()).padStart(2, '0')
            return `${idPart}${yyyy}${MM}${dd}${HH}${mm}${ss}`
        },
        openBarcodeModal() {
            this.barcodeScanning = false
            this.barcodeMode = 'product'
            this.currentVariantIndex = null
            this.barcodeModal.show()
        },
        generateAndSetBarcode() {
            this.product.barcode = this.generateBarcodeString()
            this.barcodeModal.hide()
        },
        openVariantBarcodeModal(variantIndex, mode) {
            this.barcodeScanning = mode === 'scan'
            this.barcodeMode = 'variant'
            this.currentVariantIndex = variantIndex
            this.barcodeModal.show()
            
            if (mode === 'scan') {
                this.$nextTick(() => {
                    this.startBarcodeScanner()
                })
            }
        },
        generateVariantBarcode(variantIndex) {
            const barcode = this.generateVariantBarcodeString(variantIndex)
            this.product.variants[variantIndex].barcode = barcode
        },
        generateVariantBarcodeString(variantIndex) {
            // Format: <productId or random><variant index><YYYY><MM><DD><HH><mm><ss>
            const idPart = this.product && this.product.id 
                ? String(this.product.id).substring(0, 6) 
                : String(Math.floor(100000 + Math.random() * 900000))
            const variantPart = String(variantIndex + 1).padStart(2, '0')
            const now = new Date()
            const yyyy = String(now.getFullYear()).substring(2) // Last 2 digits
            const MM = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const HH = String(now.getHours()).padStart(2, '0')
            const mm = String(now.getMinutes()).padStart(2, '0')
            const ss = String(now.getSeconds()).padStart(2, '0')
            return `${idPart}${variantPart}${yyyy}${MM}${dd}${HH}${mm}${ss}`
        },
        downloadVariantBarcode(barcodeValue) {
            this.downloadBarcodeImage(barcodeValue)
        },
        downloadBarcodeImage(value) {
            try{
                if(!value) return
                const canvas = document.createElement('canvas')
                const scale = 4
                const width = 220 * scale
                const height = 80 * scale
                canvas.width = width
                canvas.height = height
                const ctx = canvas.getContext('2d')
                // background
                ctx.fillStyle = '#ffffff'
                ctx.fillRect(0,0,width,height)
                // draw simple bars based on digits
                const digits = value.replace(/[^0-9]/g,'').split('').map(n=>parseInt(n,10))
                let x = 20
                const barWidth = 2 * scale
                const barMaxHeight = 50 * scale
                for(let i=0;i<digits.length;i++){
                    const barHeight = (digits[i] / 9) * barMaxHeight
                    ctx.fillStyle = '#000000'
                    ctx.fillRect(x, (height - barHeight - 20*scale), barWidth, barHeight)
                    x += barWidth + 1*scale
                }
                // text
                ctx.fillStyle = '#000000'
                ctx.font = `${12*scale}px monospace`
                ctx.textAlign = 'center'
                ctx.fillText(value, width/2, height - 10*scale)
                const link = document.createElement('a')
                link.href = canvas.toDataURL('image/png')
                link.download = `barcode_${value}.png`
                link.click()
            }catch(e){
                alert('Barcode download error:', e)
            }
        },
        async startBarcodeScanner() {
            try {
                this.barcodeScanning = true
                
                // Request camera access
                const stream = await navigator.mediaDevices.getUserMedia({ 
                    video: { facingMode: 'environment' } // Use back camera on mobile
                })
                
                this.barcodeStream = stream
                this.$refs.barcodeVideo.srcObject = stream
                
                // Start scanning loop
                this.scanBarcode()
            } catch (error) {
                alert('Error accessing camera:', error)
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: this.$t('camera_access_denied')
                })
                this.barcodeScanning = false
            }
        },
        stopBarcodeScanner() {
            this.barcodeScanning = false
            
            // Stop camera stream
            if (this.barcodeStream) {
                this.barcodeStream.getTracks().forEach(track => track.stop())
                this.barcodeStream = null
            }
            
            // Cancel animation frame
            if (this.barcodeAnimationFrame) {
                cancelAnimationFrame(this.barcodeAnimationFrame)
                this.barcodeAnimationFrame = null
            }
            
            // Clear video source
            if (this.$refs.barcodeVideo) {
                this.$refs.barcodeVideo.srcObject = null
            }
        },
        async scanBarcode() {
            if (!this.barcodeScanning) return
            
            const video = this.$refs.barcodeVideo
            const canvas = this.$refs.barcodeCanvas
            
            if (video.readyState === video.HAVE_ENOUGH_DATA) {
                const context = canvas.getContext('2d')
                canvas.width = video.videoWidth
                canvas.height = video.videoHeight
                context.drawImage(video, 0, 0, canvas.width, canvas.height)
                
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
                
                // Use BarcodeDetector API if available
                if ('BarcodeDetector' in window) {
                    try {
                        // eslint-disable-next-line no-undef
                        const barcodeDetector = new BarcodeDetector()
                        const barcodes = await barcodeDetector.detect(imageData)
                        
                        if (barcodes.length > 0) {
                            const barcode = barcodes[0].rawValue
                            
                            if (this.barcodeMode === 'variant' && this.currentVariantIndex !== null) {
                                this.product.variants[this.currentVariantIndex].barcode = barcode
                            } else {
                                this.product.barcode = barcode
                            }
                            
                            this.stopBarcodeScanner()
                            this.barcodeModal.hide()
                            
                            this.$swal.fire({
                                icon: 'success',
                                title: this.$t('barcode_scanned'),
                                text: barcode,
                                timer: 2000
                            })
                            return
                        }
                    } catch (error) {
                        alert('Barcode detection error:', error)
                    }
                }
            }
            
            // Continue scanning
            this.barcodeAnimationFrame = requestAnimationFrame(() => this.scanBarcode())
        },
        downloadBarcode(){
            const value = (this.product && this.product.barcode) ? String(this.product.barcode) : ''
            this.downloadBarcodeImage(value)
        },
        async handleFileUpload(event) {
            this.loading = true
            this.imageFile = null
            this.product.image = null
            const file = event.target.files[0];
            if (!file) {
                this.loading = false
                return;
            }

            // Create preview
            this.imageFile = file;
            if(file.size > maxAllowedSize) {
                this.loading = false
                return alert('File Too large please upload file less than 5mb')
            }
            this.product.image = URL.createObjectURL(file);
            try {
                await this.uploadImage()
            } catch (error) {
                this.$toast.error(error.message) 
            }
            this.loading = false
            this.event = null
        },
        async uploadImage() {
            if (!this.imageFile) return null;

            const url = await this.onUpload(this.imageFile);
            this.product.image = url
        },
        async onUpload(file){
            if(file.size > maxAllowedSize) return alert('File Too large please upload file less than 5mb')
            // if(this.business && this.business.status != 'active') return;
            return helper.customUploadFile(file,`businesses/${this.business.id}/attachments/${helper.generateRandString(20)}`,{products: true})
        },
        addVariant(){
            this.product.variants.push({
                name: '',
                sku: '',
                barcode: '',
                purchase_price: "0",
                price: "0",
                quantity: "0",
                min_quantity: "0",
                image: null
            })
        },
        removeVariant(index){
            this.product.variants.splice(index, 1)
        },
        async handleVariantFileUpload(event, index) {
            const file = event.target.files[0];
            if (!file) return;
            
            if(file.size > maxAllowedSize) {
                alert('File Too large please upload file less than 5mb')
                return;
            }
            
            this.loading = true
            try {
                const url = await this.onUpload(file);
                this.product.variants[index].image = url;
            } catch (error) {
                this.$toast.error(error.message) 
            }
            this.loading = false
            event.target.value = null;
        },
        handleFileDrop(event) {
            event.preventDefault();
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.handleFileUpload({ target: { files } });
            }
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        formatQuantity(value){
            const number = Number(value) || 0
            return number.toLocaleString(this.$i18n.locale, {maximumFractionDigits: 3})
        },
    },
    computed : {
        visibleColumns() {
            return {
                ...getInventoryProductColumnDefaults(),
                ...(this.business?.metadata?.inventory_products_columns || {})
            };
        },
        formattedInsightsPeriod(){
            if(!this.start || !this.end) return this.$t('all_time')
            return `${moment(this.start).format('DD/MM/YYYY')} - ${moment(this.end).format('DD/MM/YYYY')}`
        },
        productDisplayUnit(){
            return this.product.unity || this.product.inventories?.[0]?.unity || this.$t('units')
        },
        helper(){ return helper},
        business(){ return this.$store.getters.BUSINESS },
        hasAccess(permission){
            return this.$store.getters.HAS_PERMISSION_AND_MODULE_ENABLED(permission)
        },
    isFormValid() {
      // Check required fields
      const hasLabel = this.product.label && this.product.label.length >= 2;
      
      // If product has variants, validate variants instead
      if (this.product.has_variants) {
        if (this.product.variants.length === 0) return false;
        
        // All variants must have name, purchase_price, price, and quantity
        const allVariantsValid = this.product.variants.every(variant => {
          return variant.name && 
                 variant.name.length >= 2 &&
                 variant.purchase_price !== null && 
                 variant.purchase_price !== undefined && 
                 parseFloat(variant.purchase_price) >= 0 &&
                 variant.price !== null && 
                 variant.price !== undefined && 
                 parseFloat(variant.price) >= 0 &&
                 variant.quantity !== null && 
                 variant.quantity !== undefined && 
                 parseFloat(variant.quantity) >= 0;
        });
        return hasLabel && allVariantsValid;
      } else {
        return hasLabel;
      }
    }
    }
}
</script>

<style scoped>
    .product-insights-card,
    .product-overview-card {
        border: 1px solid #e4e9ef;
        border-radius: 12px;
        box-shadow: 0 4px 16px rgba(35, 51, 70, 0.04);
        overflow: hidden;
    }

    .product-insights-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        padding: 12px 16px;
        border-bottom: 1px solid #edf0f4;
    }

    .product-insights-header h6,
    .product-overview-card .card-header h6 {
        display: flex;
        align-items: center;
        color: #29394d;
        font-size: 0.9rem;
        font-weight: 650;
    }

    .product-section-icon {
        width: 20px;
        margin-right: 8px;
        color: #3568d4;
        font-size: 1rem !important;
        line-height: 1;
        text-align: center;
    }

    .product-overview-card .card-header h6 > i {
        width: 18px;
        flex: 0 0 18px;
        color: #3568d4;
        font-size: 0.9rem !important;
        line-height: 1;
        text-align: center;
    }

    .product-insights-filters {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 8px;
    }

    .product-insights-filters .form-select {
        width: 145px;
        min-height: 36px;
        font-size: 0.82rem;
    }

    .product-insights-date-picker {
        width: 205px;
        min-width: 205px;
    }

    ::v-deep .product-insights-date-picker .mx-input {
        height: 36px;
        font-size: 0.82rem;
    }

    .product-insights-card .card-body {
        padding: 12px 16px 16px;
    }

    .product-insights-loading {
        position: absolute;
        inset: 0;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.72);
    }

    .product-metric {
        min-height: 70px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border: 1px solid #e8ecf1;
        border-radius: 9px;
        background: #fbfcfd;
    }

    .product-metric-icon {
        width: 34px;
        height: 34px;
        flex: 0 0 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background: #eef2f6;
        color: #40546c;
        font-size: 0.95rem;
    }

    .product-metric-icon i {
        display: block;
        width: 18px;
        height: 18px;
        font-size: 1rem !important;
        line-height: 18px;
        text-align: center;
    }

    .product-metric-icon i::before {
        display: block;
        line-height: 18px;
    }

    .product-metric-icon--stock {
        color: #3568d4;
        background: #eaf0ff;
    }

    .product-metric-icon--sales {
        color: #17845a;
        background: #e7f7ef;
    }

    .product-metric-icon--sold {
        color: #7357c7;
        background: #f0ecff;
    }

    .product-metric-icon--value {
        color: #bd7419;
        background: #fff3df;
    }

    .product-metric small,
    .product-metric strong,
    .product-metric span {
        display: block;
    }

    .product-metric small {
        margin-bottom: 1px;
        color: #738094;
        font-size: 0.74rem;
    }

    .product-metric strong {
        color: #26374c;
        font-size: 0.96rem;
        line-height: 1.25;
    }

    .product-metric span.text-danger {
        margin-top: 2px;
        font-size: 0.68rem;
        font-weight: 600;
    }

    .product-overview-card .card-header {
        min-height: 45px;
        padding: 11px 14px;
        border-bottom: 1px solid #edf0f4;
    }

    .product-overview-card .card-header .badge {
        border: 1px solid #e2e7ed;
        font-size: 0.7rem;
        font-weight: 500;
    }

    .product-table-wrap {
        max-height: 360px;
        overflow: auto;
    }

    .product-insights-table {
        min-width: 620px;
        border-collapse: separate;
        border-spacing: 0;
        color: #2d3b4f;
        font-size: 0.82rem;
    }

    .product-insights-table thead th {
        position: sticky;
        top: 0;
        z-index: 1;
        padding: 10px 14px;
        border-bottom: 1px solid #dfe4ea;
        background: #f8f9fb;
        color: #606d7e;
        font-size: 0.73rem;
        font-weight: 600;
        text-align: center;
        vertical-align: middle;
        white-space: nowrap;
    }

    .product-insights-table thead th small,
    .product-insights-table tbody td:first-child strong,
    .product-insights-table tbody td:first-child small {
        display: block;
    }

    .product-insights-table thead th small {
        margin-top: 1px;
        color: #8691a0;
        font-size: 0.66rem;
        font-weight: 500;
    }

    .product-insights-table tbody td {
        padding: 12px 14px;
        border-color: #ebeff3;
        text-align: center;
        vertical-align: middle;
    }

    .product-insights-table tbody td:first-child strong {
        color: #293b52;
        font-size: 0.76rem;
        font-weight: 650;
    }

    .product-insights-table tbody td:first-child small {
        margin-top: 2px;
        color: #8490a0;
        font-size: 0.68rem;
    }

    .product-empty-state {
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        color: #8793a2;
        font-size: 0.85rem;
        text-align: center;
    }

    .product-overview-card .card-body {
        padding: 10px 14px 14px;
    }

    .product-info-list > div {
        display: grid;
        grid-template-columns: minmax(76px, 0.7fr) minmax(0, 1.3fr);
        align-items: baseline;
        gap: 10px;
        padding: 7px 0;
        border-bottom: 1px solid #f0f2f5;
    }

    .product-info-list dt,
    .product-info-list dd {
        margin: 0;
        font-size: 0.82rem;
        overflow-wrap: anywhere;
    }

    .product-info-list dt {
        color: #7b8797;
        font-weight: 500;
    }

    .product-info-list dd {
        color: #2d3b4f;
        font-weight: 600;
        text-align: end;
    }

    .product-info-list dd span,
    .product-info-list dd small {
        display: block;
    }

    .product-info-list dd small {
        margin-top: 2px;
        color: #8994a2;
        font-size: 0.68rem;
        font-weight: 500;
    }

    .product-notes {
        margin-top: 12px;
        padding: 10px;
        border-radius: 7px;
        background: #f8f9fb;
        color: #3f4e61;
        font-size: 0.8rem;
    }

    .product-overview-image {
        max-width: 100%;
        max-height: 230px;
        object-fit: contain;
        border-radius: 8px;
    }

    @media (max-width: 767.98px) {
        .product-insights-header {
            align-items: stretch;
            flex-direction: column;
        }

        .product-insights-filters,
        .product-insights-filters .form-select,
        .product-insights-date-picker {
            width: 100%;
            min-width: 0;
        }

        .product-insights-filters {
            flex-wrap: wrap;
        }
    }

    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }

    .section-header-compact h6 {
        color: #495057;
        font-size: 0.85rem;
        font-weight: 600;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 0.25rem;
    }

    .form-label-compact {
        font-size: 0.8rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.25rem;
        display: block;
    }

    .drop-zone-compact {
        border: 2px dashed #dee2e6;
        border-radius: 8px;
        padding: 15px;
        text-align: center;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 280px;
        background: #f8f9fa;
    }

    .drop-zone-compact:hover:not(.disabled) {
        border-color: #0d6efd;
        background: #e7f1ff;
        transform: translateY(-2px);
        box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    }

    .drop-zone-compact.disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    .drop-zone-compact.has-image {
        padding: 0;
        border: 2px solid #dee2e6;
        background: white;
    }

    .drop-zone-compact.has-image:hover {
        border-color: #0d6efd;
    }

    .preview-image-compact {
        width: 100%;
        height: 280px;
        object-fit: cover;
        border-radius: 6px;
    }

    .drop-zone-content {
        color: #6c757d;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
    }

    .drop-zone-content i {
        color: #0d6efd;
        font-size: 18px !important;
    }

    a.text-decoration-none {
        color: #303030;
        transition: color 0.2s;
        font-size: 0.75rem;
    }

    a.text-decoration-none:hover {
        color: #0d6efd;
    }

    /* Variant Styles */
    .variants-section {
        margin-top: 0.5rem;
    }

    .variant-card {
        animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    .variant-card .card {
        transition: all 0.2s ease;
    }

    .variant-card .card:hover {
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    .variant-preview-image {
        width: 100px;
        height: 100px;
        object-fit: cover;
        border-radius: 6px;
        border: 2px solid #dee2e6;
        display: block;
    }

    .variant-image-upload {
        padding: 0.75rem;
        background: #f8f9fa;
        border-radius: 6px;
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
